import React, { useState } from 'react';
import { Check } from 'lucide-react';
import T from 'Components/Helpers/translate';
import ContactResource from 'Resources/Contact';
import type Contact from 'App/resources/Model/Contact';
import type { FoundContactDetails } from './FindContactDetails';

type State = 'idle' | 'saving' | 'filled' | 'nothingToFill' | 'error';

/**
 * "Add to the contact" under Find email and phone, when the profile already is a contact
 * (WEB-13446). Fills only what the contact lacks - the email, and the mobile number in `cellPhone`,
 * where the Add flow puts a found phone too - and never overwrites a value someone entered.
 */
type Props = { contactId: number; found: FoundContactDetails; onFilled?: () => void };

const FillExistingContact: React.FC<Props> = ({ contactId, found, onFilled }) => {
	const [state, setState] = useState<State>('idle');

	const fill = async () => {
		setState('saving');
		try {
			const { data: contact } = await ContactResource.get(contactId);
			const missing: Partial<Contact> = {
				...(found.email && !contact.email ? { email: found.email } : {}),
				...(found.phone && !contact.cellPhone ? { cellPhone: found.phone } : {})
			};
			if (!Object.keys(missing).length) {
				setState('nothingToFill');
				return;
			}
			await ContactResource.save({ id: contactId, ...missing });
			setState('filled');
			onFilled?.();
		} catch (err) {
			console.error('[EmbedAIChat] filling the contact failed', err);
			setState('error');
		}
	};

	if (state === 'filled') {
		return (
			<span className="embed-add-fv-label">
				<Check />
				{T('assistant.embedPersonFilled')}
			</span>
		);
	}
	if (state === 'nothingToFill') {
		return <span className="embed-add-fv-label">{T('assistant.embedPersonNothingToFill')}</span>;
	}
	return (
		<>
			{state === 'error' ? <div className="embed-add-error">{T('assistant.embedAddSaveError')}</div> : null}
			<button type="button" className="embed-add-btn" disabled={state === 'saving'} onClick={() => void fill()}>
				{T('assistant.embedPersonFillContact')}
			</button>
		</>
	);
};

export default FillExistingContact;
